<template>
    <div class="fsr">
        <div class="fsr-head">
            <div class="fsr-head-text">
                <span class="fsr-title">{{ $t('forms.status.reasons.title') }}</span>
                <span class="fsr-hint">{{ $t('forms.status.reasons.hint') }}</span>
            </div>
            <button
                type="button"
                class="fsr-add"
                :disabled="!statusId || creating"
                @click="startCreate"
            >
                <i class="fal fa-plus"></i>
                <span>{{ $t('forms.status.reasons.add') }}</span>
            </button>
        </div>

        <p v-if="!statusId" class="fsr-empty">
            <i class="fal fa-info-circle"></i>
            {{ $t('forms.status.reasons.save_first') }}
        </p>

        <template v-else>
            <div v-if="loading" class="fsr-loading">
                <BmvLoader :active="true" />
            </div>

            <p v-else-if="!reasons.length && !creating" class="fsr-empty">
                <i class="fal fa-comment-alt-lines"></i>
                {{ $t('forms.status.reasons.none') }}
            </p>

            <ul v-else class="fsr-list">
                <li
                    v-for="(reason, index) in reasons"
                    :key="reason.id"
                    class="fsr-item"
                    :class="{ 'fsr-item--inactive': !reason.active }"
                >
                    <div class="fsr-move">
                        <button
                            type="button"
                            class="fsr-move-btn"
                            :disabled="index === 0 || busy"
                            :aria-label="$t('forms.status.reasons.move_up')"
                            @click="move(index, -1)"
                        >
                            <i class="fal fa-chevron-up"></i>
                        </button>
                        <button
                            type="button"
                            class="fsr-move-btn"
                            :disabled="index === reasons.length - 1 || busy"
                            :aria-label="$t('forms.status.reasons.move_down')"
                            @click="move(index, 1)"
                        >
                            <i class="fal fa-chevron-down"></i>
                        </button>
                    </div>

                    <div class="fsr-body">
                        <template v-if="editing && editing.id === reason.id">
                            <div v-for="lang in langs" :key="lang" class="fsr-edit-field">
                                <span class="fsr-lang">{{ lang }}</span>
                                <input
                                    v-model="editing.translations[lang]"
                                    type="text"
                                    class="fsr-input"
                                    :placeholder="$t('forms.status.reasons.placeholder')"
                                />
                            </div>
                            <p v-if="error" class="fsr-error">{{ error }}</p>
                        </template>
                        <template v-else>
                            <span class="fsr-label">{{ reasonLabel(reason) }}</span>
                            <span v-if="secondaryLabel(reason)" class="fsr-label-alt">
                                {{ secondaryLabel(reason) }}
                            </span>
                        </template>
                    </div>

                    <div class="fsr-actions">
                        <template v-if="editing && editing.id === reason.id">
                            <button type="button" class="fsr-btn fsr-btn--primary" :disabled="busy" @click="saveEdit">
                                <i class="fal fa-check"></i>
                                <span>{{ $t('common.save') }}</span>
                            </button>
                            <button type="button" class="fsr-btn" :disabled="busy" @click="cancelEdit">
                                {{ $t('common.cancel') }}
                            </button>
                        </template>
                        <template v-else>
                            <label class="fsr-switch" :title="$t('forms.status.reasons.active')">
                                <input
                                    type="checkbox"
                                    :checked="reason.active"
                                    :disabled="busy"
                                    @change="toggleActive(reason)"
                                />
                                <span class="fsr-switch-track"></span>
                            </label>
                            <button
                                type="button"
                                class="fsr-btn"
                                :disabled="busy"
                                :aria-label="$t('common.edit')"
                                @click="startEdit(reason)"
                            >
                                <i class="fal fa-edit"></i>
                            </button>
                            <button
                                type="button"
                                class="fsr-btn fsr-btn--danger"
                                :disabled="busy"
                                :aria-label="$t('common.delete')"
                                @click="remove(reason)"
                            >
                                <i class="fal fa-trash-alt"></i>
                            </button>
                        </template>
                    </div>
                </li>

                <li v-if="creating" class="fsr-item fsr-item--new">
                    <div class="fsr-move fsr-move--placeholder">
                        <i class="fal fa-plus-circle"></i>
                    </div>
                    <div class="fsr-body">
                        <div v-for="lang in langs" :key="lang" class="fsr-edit-field">
                            <span class="fsr-lang">{{ lang }}</span>
                            <input
                                v-model="editing.translations[lang]"
                                type="text"
                                class="fsr-input"
                                :placeholder="$t('forms.status.reasons.placeholder')"
                            />
                        </div>
                        <p v-if="error" class="fsr-error">{{ error }}</p>
                    </div>
                    <div class="fsr-actions">
                        <button type="button" class="fsr-btn fsr-btn--primary" :disabled="busy" @click="saveEdit">
                            <i class="fal fa-check"></i>
                            <span>{{ $t('common.save') }}</span>
                        </button>
                        <button type="button" class="fsr-btn" :disabled="busy" @click="cancelEdit">
                            {{ $t('common.cancel') }}
                        </button>
                    </div>
                </li>
            </ul>

            <p v-if="creating && !reasons.length" class="fsr-empty fsr-empty--muted">
                {{ $t('forms.status.reasons.none') }}
            </p>
        </template>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'
import BmvLoader from '@/components/UI/v2/BmvLoader.vue'
import { reasonTranslations, reasonLabel } from '@/helpers/statusReasons'

export default {
    name: 'StatusReasons',
    components: { BmvLoader },
    props: {
        formId: { type: Number, required: true },
        // Null while creating a status: reasons hang off a saved status.
        statusId: { type: Number, default: null }
    },
    data() {
        return {
            reasons: [],
            loading: false,
            busy: false,
            creating: false,
            editing: null,
            error: ''
        }
    },
    computed: {
        ...mapState({ langs: (state) => state.langs })
    },
    watch: {
        statusId: {
            immediate: true,
            handler(id) {
                this.reasons = []
                this.cancelEdit()
                if (id) this.fetch()
            }
        }
    },
    methods: {
        ...mapActions('form', [
            'getStatusReasons',
            'createStatusReason',
            'updateStatusReason',
            'deleteStatusReason'
        ]),
        reasonLabel(reason) {
            return reasonLabel(reason, this.$i18n.locale, this.langs[0])
        },
        // Second language, shown greyed out under the primary label.
        secondaryLabel(reason) {
            const translations = reasonTranslations(reason)
            const other = this.langs.find((lang) => lang !== this.$i18n.locale)
            if (!other) return ''
            const label = translations[other]
            return label && label !== this.reasonLabel(reason) ? label : ''
        },
        fetch() {
            this.loading = true
            this.getStatusReasons({ formId: this.formId, statusId: this.statusId })
                .then((reasons) => {
                    this.reasons = this.sort(reasons)
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        sort(reasons) {
            return [...reasons].sort((a, b) => {
                const order = (a.order || 0) - (b.order || 0)
                return order !== 0 ? order : a.id - b.id
            })
        },
        emptyTranslations() {
            const translations = {}
            this.langs.forEach((lang) => {
                translations[lang] = ''
            })
            return translations
        },
        startCreate() {
            this.error = ''
            this.editing = { id: null, translations: this.emptyTranslations() }
            this.creating = true
        },
        startEdit(reason) {
            this.error = ''
            this.creating = false
            const translations = this.emptyTranslations()
            const loaded = reasonTranslations(reason)
            this.langs.forEach((lang) => {
                translations[lang] = loaded[lang] || ''
            })
            this.editing = { id: reason.id, translations }
        },
        cancelEdit() {
            this.editing = null
            this.creating = false
            this.error = ''
        },
        // The first language is the reference one (fr), required by the API.
        validate() {
            const primary = this.langs[0]
            if (!this.editing.translations[primary]) {
                this.error = this.$t('forms.status.reasons.label_required')
                return false
            }
            return true
        },
        saveEdit() {
            if (!this.validate()) return

            const translations = {}
            this.langs.forEach((lang) => {
                const value = (this.editing.translations[lang] || '').trim()
                if (value) translations[lang] = value
            })

            this.busy = true
            this.error = ''
            const isCreating = this.creating
            const payload = isCreating
                ? { translations, order: this.reasons.length, active: true }
                : { id: this.editing.id, translations }

            const request = isCreating
                ? this.createStatusReason({
                      formId: this.formId,
                      statusId: this.statusId,
                      reason: payload
                  })
                : this.updateStatusReason({
                      formId: this.formId,
                      statusId: this.statusId,
                      reason: payload
                  })

            request
                .then(() => {
                    this.cancelEdit()
                    this.fetch()
                    this.$emit('changed')
                })
                .catch((error) => {
                    this.error = this.firstValidationError(error)
                    if (!this.error) this.$store.dispatch('dispatchError', error)
                })
                .finally(() => {
                    this.busy = false
                })
        },
        toggleActive(reason) {
            this.busy = true
            this.updateStatusReason({
                formId: this.formId,
                statusId: this.statusId,
                reason: { id: reason.id, active: !reason.active }
            })
                .then(() => {
                    this.fetch()
                    this.$emit('changed')
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                    // Put the checkbox back in sync with the server.
                    this.fetch()
                })
                .finally(() => {
                    this.busy = false
                })
        },
        async remove(reason) {
            const ok = await this.$bmvDialog.confirm({
                title: this.$t('forms.status.reasons.delete_confirm_title'),
                message: this.$t('forms.status.reasons.delete_confirm_message', {
                    reason: this.reasonLabel(reason)
                }),
                variant: 'danger',
                confirmLabel: this.$t('common.delete'),
                cancelLabel: this.$t('common.cancel')
            })
            if (!ok) return

            this.busy = true
            this.deleteStatusReason({
                formId: this.formId,
                statusId: this.statusId,
                reasonId: reason.id
            })
                .then(() => {
                    this.fetch()
                    this.$emit('changed')
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
                .finally(() => {
                    this.busy = false
                })
        },
        // Reordering swaps the two `order` values: the list is sorted by order
        // then id, so both rows have to be written for the swap to stick.
        move(index, direction) {
            const target = index + direction
            if (target < 0 || target >= this.reasons.length) return

            const moved = this.reasons[index]
            const swapped = this.reasons[target]

            this.busy = true
            Promise.all([
                this.updateStatusReason({
                    formId: this.formId,
                    statusId: this.statusId,
                    reason: { id: moved.id, order: target }
                }),
                this.updateStatusReason({
                    formId: this.formId,
                    statusId: this.statusId,
                    reason: { id: swapped.id, order: index }
                })
            ])
                .then(() => {
                    this.fetch()
                    this.$emit('changed')
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
                .finally(() => {
                    this.busy = false
                })
        },
        firstValidationError(error) {
            const errors = error && error.response && error.response.data && error.response.data.errors
            if (!errors) return ''
            const first = Object.keys(errors)[0]
            return first ? [].concat(errors[first])[0] : ''
        }
    }
}
</script>

<style scoped>
.fsr {
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-1, #f8fafc);
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.fsr-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.fsr-head-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.fsr-title {
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--v2-text-1);
}

.fsr-hint {
    font-size: 0.75rem;
    line-height: 1.4;
    color: var(--v2-text-3);
}

.fsr-add {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    height: 30px;
    padding: 0 12px;
    border: 1px solid var(--v2-primary);
    border-radius: var(--v2-radius-sm);
    background: var(--v2-primary);
    color: #fff;
    font-size: 0.78rem;
    font-weight: 500;
    cursor: pointer;
}

.fsr-add i {
    color: #fff !important;
}

.fsr-add:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.fsr-empty {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 0.78rem;
    color: var(--v2-text-3);
}

.fsr-empty--muted {
    opacity: 0.7;
}

.fsr-loading {
    display: flex;
    justify-content: center;
    padding: 12px 0;
}

.fsr-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.fsr-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #fff;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
}

.fsr-item--inactive .fsr-label {
    color: var(--v2-text-3);
    text-decoration: line-through;
}

.fsr-move {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 0 0 auto;
}

.fsr-move--placeholder {
    color: var(--v2-text-3);
    font-size: 0.9rem;
}

.fsr-move-btn {
    width: 22px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: var(--v2-text-3);
    font-size: 0.7rem;
    cursor: pointer;
    padding: 0;
}

.fsr-move-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.fsr-body {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.fsr-label {
    font-size: 0.82rem;
    color: var(--v2-text-1);
    word-break: break-word;
}

.fsr-label-alt {
    font-size: 0.72rem;
    color: var(--v2-text-3);
    word-break: break-word;
}

.fsr-edit-field {
    display: flex;
    align-items: center;
    gap: 8px;
}

.fsr-lang {
    flex: 0 0 auto;
    text-transform: uppercase;
    font-size: 0.66rem;
    font-weight: 700;
    color: var(--v2-text-3);
    min-width: 20px;
}

.fsr-input {
    flex: 1 1 auto;
    min-width: 0;
    height: 30px;
    padding: 0 8px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    font-size: 0.8rem;
    color: var(--v2-text-1);
    background: #fff;
}

.fsr-input:focus {
    outline: none;
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.15);
}

.fsr-error {
    margin: 2px 0 0;
    font-size: 0.72rem;
    color: #dc2626;
}

.fsr-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
}

.fsr-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 28px;
    padding: 0 9px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    background: #fff;
    color: var(--v2-text-2);
    font-size: 0.76rem;
    cursor: pointer;
}

.fsr-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.fsr-btn--primary {
    background: var(--v2-primary);
    border-color: var(--v2-primary);
    color: #fff;
}

.fsr-btn--primary i {
    color: #fff !important;
}

.fsr-btn--danger:hover:not(:disabled) {
    border-color: #dc2626;
    color: #dc2626;
}

.fsr-switch {
    position: relative;
    display: inline-flex;
    flex: 0 0 auto;
    width: 34px;
    height: 18px;
    margin: 0;
    cursor: pointer;
}

.fsr-switch input {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    cursor: pointer;
}

.fsr-switch-track {
    width: 100%;
    height: 100%;
    border-radius: 999px;
    background: #cbd5e1;
    transition: background 0.15s ease;
}

.fsr-switch-track::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.15s ease;
}

.fsr-switch input:checked + .fsr-switch-track {
    background: var(--v2-primary);
}

.fsr-switch input:checked + .fsr-switch-track::after {
    transform: translateX(16px);
}

@media (max-width: 640px) {
    .fsr-head {
        flex-direction: column;
        align-items: stretch;
    }

    .fsr-add {
        align-self: flex-start;
    }

    .fsr-item {
        flex-wrap: wrap;
    }

    .fsr-body {
        flex: 1 1 100%;
        order: 1;
    }

    .fsr-move {
        flex-direction: row;
        order: 2;
    }

    .fsr-move-btn {
        width: 28px;
        height: 24px;
    }

    .fsr-actions {
        order: 3;
        margin-left: auto;
    }
}
</style>
